import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Button,
  GET_COMPLETE_CURRENT_USER,
  PhoneNumberList,
  phoneNumberListIsValid,
  UPDATE_CURRENT_USER,
} from '@employ-engage/common-components';
import { PhoneNumberDraft, UserPhoneNumber } from '@employ-engage/data';

/**
 * The signed-in person's own phone numbers: add, edit and remove them.
 *
 * Saved on its own, with its own button, rather than with the personal
 * information form above it, whose save does not yet write anything. Nothing
 * texts a user, so there is no consent to give here.
 */
export function UserPhoneNumbers() {
  const { t } = useTranslation(['common']);
  const { data, loading } = useQuery(GET_COMPLETE_CURRENT_USER);
  const [updateCurrentUser, { loading: saving }] = useMutation(UPDATE_CURRENT_USER);
  const [drafts, setDrafts] = useState<PhoneNumberDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState('');
  const [problem, setProblem] = useState('');

  const saved: UserPhoneNumber[] = data?.me?.phoneNumbers ?? [];
  useEffect(() => {
    if (!dirty) setDrafts(saved.map(({ id, number, label }) => ({ id, number, label })));
    // Only when the saved list changes, not on every edit.
  }, [data]);

  if (loading) return null;

  const valid = phoneNumberListIsValid(drafts);

  return (
    <form
      className="sm:max-w-xl"
      onSubmit={async (e) => {
        e.preventDefault();
        setNotice('');
        setProblem('');
        try {
          await updateCurrentUser({
            variables: {
              input: {
                phoneNumbers: drafts.map(({ id, number, label }) => ({
                  ...(id ? { id } : {}),
                  number: number.trim(),
                  label,
                })),
              },
            },
          });
          setDirty(false);
          setNotice(t('Your phone numbers are saved.'));
        } catch (error: any) {
          setProblem(error?.message ?? t('Something went wrong. Please try again.'));
        }
      }}
    >
      <PhoneNumberList
        legend={t('Your phone numbers')}
        value={drafts}
        onChange={(list) => {
          setDrafts(list);
          setDirty(true);
        }}
        disabled={saving}
      />
      <div className="mt-4 flex items-center gap-3">
        <Button
          type="submit"
          variant="primary"
          className="min-h-[44px] text-base"
          disabled={!dirty || !valid || saving}
        >
          {t('Save phone numbers')}
        </Button>
        <span role="status" className="text-base text-green-800">
          {notice}
        </span>
        <span role="alert" className="text-base text-red-700">
          {problem}
        </span>
      </div>
    </form>
  );
}

export default UserPhoneNumbers;
